/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer, useMemo } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Material Dashboard 2 React main context
const MaterialUI = createContext();

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

// Material Dashboard 2 React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    case "OPENSIDE": {
      return { ...state, openSide: action.value };
    }
    
    case "NOMBRENEGOCIO": {
      return { ...state, nombreNegocio: action.value };
    }
    
    case "ACCESOWEB": {
      return { ...state, accesoWeb: action.value };
    }

    case "ROLEWEB": {
      return { ...state, roleWeb: action.value };
    }
    
    case "ROLDETALLES": {
      return { ...state, roleDetalles: action.value };
    }

    case "MODULOS": {
      return { ...state, modulos: action.value };
    }
    
    case "USUARIOLOG": {
      return { ...state, usuarioLog: action.value };
    }
    
    case "CLAVELOG": {
      return { ...state, claveLog: action.value };
    }

    case "ANCHOCODIGOBARRA": {
      return { ...state, anchoCodigoBarra: action.value };
    }

    case "ALTOCODIGOBARRA": {
      return { ...state, altoCodigoBarra: action.value };
    }


    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
    openSide: true,
    nombreNegocio: "false",
    accesoWeb: false,
    roleWeb: "false",
    roleDetalles: [],
    modulos: "false",
    usuarioLog: "",
    claveLog: "",
    anchoCodigoBarra: 0,
    altoCodigoBarra: 0,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  /** Alineado con breakpoints por defecto de MUI (sm: 600, md: 900). */
  const isMobile = useMediaQuery("(max-width:599.95px)");
  const isMdDown = useMediaQuery("(max-width:899.95px)");

  const controllerWithViewport = useMemo(
    () => ({
      ...controller,
      viewport: {
        isMobile,
        isMdDown,
      },
    }),
    [controller, isMobile, isMdDown]
  );

  const value = useMemo(
    () => [controllerWithViewport, dispatch],
    [controllerWithViewport, dispatch]
  );

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);
  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });
const setOpenSide = (dispatch, value) => dispatch({ type: "OPENSIDE", value });
const setNombrenegocio = (dispatch, value) => dispatch({ type: "NOMBRENEGOCIO", value });
const setAccesoweb = (dispatch, value) => dispatch({ type: "ACCESOWEB", value });
const setRoleweb = (dispatch, value) => dispatch({ type: "ROLEWEB", value });
const setRoleDetalles = (dispatch, value) => dispatch({ type: "ROLDETALLES", value });
const setModulos = (dispatch, value) => dispatch({ type: "MODULOS", value });
const setUsuarioLog = (dispatch, value) => dispatch({ type: "USUARIOLOG", value });
const setClaveLog = (dispatch, value) => dispatch({ type: "CLAVELOG", value });
const setAnchoCodigoBarra = (dispatch, value) => dispatch({ type: "ANCHOCODIGOBARRA", value });
const setAltoCodigoBarra = (dispatch, value) => dispatch({ type: "ALTOCODIGOBARRA", value });

export { GlobalMDAlertProvider, useGlobalMDAlert } from "./GlobalMDAlertContext";

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setOpenSide,
  setNombrenegocio,
  setAccesoweb,
  setRoleweb,
  setRoleDetalles,
  setModulos,
  setUsuarioLog,
  setClaveLog,
  setAnchoCodigoBarra,
  setAltoCodigoBarra,
};
